/**
 * Long-form site article stored at articles/{id}/web/content.
 * The mobile app and the web app keep using cards. They do not read this.
 */

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "a",
  "strong",
  "em",
  "blockquote",
  "img",
  "figure",
  "figcaption",
  "hr",
]);

const DROP_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "svg",
  "link",
  "meta",
]);

const SITE_SLUG_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

export function emptySiteForm() {
  return {
    status: "draft",
    bodyHtml: "",
    excerpt: "",
    seoTitle: "",
    seoDescription: "",
    ogImageUrl: "",
    noindex: false,
  };
}

export function siteFormFromDoc(data = {}) {
  const text = (value) => (typeof value === "string" ? value : "");
  const mapEn = (value) =>
    value && typeof value === "object" ? text(value.en) : text(value);
  return {
    status: data.status === "published" ? "published" : "draft",
    bodyHtml: mapEn(data.bodyHtml),
    excerpt: mapEn(data.excerpt),
    seoTitle: mapEn(data.seoTitle),
    seoDescription: mapEn(data.seoDescription),
    ogImageUrl: text(data.ogImageUrl),
    noindex: data.noindex === true,
  };
}

function safeHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\s*javascript:/i.test(raw) || /^\s*data:/i.test(raw)) return "";
  try {
    const url = new URL(raw, "https://example.invalid");
    if (url.protocol === "http:" || url.protocol === "https:") return url.href;
    if (url.protocol === "mailto:" && url.pathname.includes("@")) return raw;
  } catch {
    return "";
  }
  return "";
}

function safeImageSrc(value) {
  const raw = String(value || "").trim();
  if (!raw.toLowerCase().startsWith("https://")) return "";
  if (raw.toLowerCase().startsWith("https://data:")) return "";
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

function renameElement(el, tagName) {
  const next = el.ownerDocument.createElement(tagName);
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

function applyAttrs(el, tag) {
  const saved = {};
  [...el.attributes].forEach((attr) => {
    saved[attr.name.toLowerCase()] = attr.value;
  });
  [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));

  if (tag === "a") {
    const href = safeHref(saved.href);
    if (!href) return "unwrap";
    el.setAttribute("href", href);
    if (/^https?:/i.test(href)) {
      el.setAttribute("rel", "noopener noreferrer");
      el.setAttribute("target", "_blank");
    }
    return "keep";
  }

  if (tag === "img") {
    const src = safeImageSrc(saved.src);
    if (!src) return "remove";
    el.setAttribute("src", src);
    el.setAttribute("alt", String(saved.alt || "").replace(/[<>"]/g, ""));
    return "keep";
  }

  return "keep";
}

function sanitizeElementChildren(parent) {
  let child = parent.firstChild;
  while (child) {
    if (child.nodeType === Node.TEXT_NODE) {
      child = child.nextSibling;
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      const skip = child.nextSibling;
      child.remove();
      child = skip;
      continue;
    }

    let el = child;
    const rawTag = el.tagName.toLowerCase();
    if (DROP_TAGS.has(rawTag)) {
      const skip = el.nextSibling;
      el.remove();
      child = skip;
      continue;
    }
    if (rawTag === "b") el = renameElement(el, "strong");
    else if (rawTag === "i") el = renameElement(el, "em");

    const tag = el.tagName.toLowerCase();
    const next = el.nextSibling;
    if (!ALLOWED_TAGS.has(tag)) {
      const firstMoved = el.firstChild;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      el.remove();
      child = firstMoved || next;
      continue;
    }

    sanitizeElementChildren(el);
    const action = applyAttrs(el, tag);
    if (action === "remove") {
      el.remove();
      child = next;
      continue;
    }
    if (action === "unwrap") {
      const firstMoved = el.firstChild;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      el.remove();
      child = firstMoved || next;
      continue;
    }
    child = next;
  }
}

/** Browser allowlist for the site article body. */
export function sanitizeSiteArticleHtml(raw) {
  if (!raw || typeof raw !== "string") return "";
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(
    `<body>${raw}</body>`,
    "text/html",
  );
  sanitizeElementChildren(doc.body);
  return doc.body.innerHTML.trim();
}

export function isEmptySiteHtml(html) {
  const sanitized = sanitizeSiteArticleHtml(html);
  if (/<img\b/i.test(sanitized)) return false;
  const text = sanitized
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
  return text.length === 0;
}

export function buildSiteContentWrite(site = {}) {
  const source = { ...emptySiteForm(), ...site };
  const trim = (value) => String(value || "").trim();
  return {
    status: source.status === "published" ? "published" : "draft",
    bodyHtml: { en: sanitizeSiteArticleHtml(source.bodyHtml) },
    excerpt: { en: trim(source.excerpt) },
    seoTitle: { en: trim(source.seoTitle) },
    seoDescription: { en: trim(source.seoDescription) },
    ogImageUrl: trim(source.ogImageUrl),
    noindex: Boolean(source.noindex),
  };
}

export function siteContentShouldPersist(showOnSite, write) {
  if (!write) return false;
  if (showOnSite) return true;
  if (write.status === "published" || write.noindex) return true;
  return Boolean(
    write.bodyHtml?.en ||
      write.excerpt?.en ||
      write.seoTitle?.en ||
      write.seoDescription?.en ||
      write.ogImageUrl,
  );
}

export function sitePublishError({ showOnSite, slugEn, site, ogInvalid }) {
  if (ogInvalid) return "OG image must be a full HTTPS URL.";
  if (!showOnSite || site?.status !== "published") return "";
  const slug = String(slugEn || "").trim();
  if (!slug) return "English slug is required to publish on the site.";
  if (!SITE_SLUG_RE.test(slug)) {
    return "English slug should use lowercase letters, numbers, and hyphens.";
  }
  if (isEmptySiteHtml(site.bodyHtml || "")) {
    return "Site article body is required to publish.";
  }
  return "";
}
